// src/features/media/MediaApi.ts

import { webApi } from '@/shared/api/WebApi';
import { ApiError } from '@/shared/api/ApiError';
import type { IListRes } from '@lib2/model/common/ListRes';
import type {
  VideoViewData,
  VideoUpdateRequest,
  ClipCreateRequest,
  TextTrackCreateRequest,
  MediaPosterIngestRequest,
  IExtractMediaPosterReq,
  MergeCreateRequest,
  MediaTagsBatchRequest,
  MediaTagsBatchResponse,
} from '@/lib/types/Media';
import type { TranscodeVodData } from '@/lib/types/TranscodeVod';
import type { ClientIngest } from '@/lib/types/Upload';

/**
 * Media API endpoints.
 * Static class for consistent naming and namespacing.
 */
export class MediaApi {
  /**
   * Fetch all videos for a customer.
   * Returns { data: VideoViewData[], hasMore: boolean }
   */
  static async getVideos(cId: number): Promise<IListRes<VideoViewData>> {
    return webApi.get<IListRes<VideoViewData>>('media/video', { cId });
  }

  /**
   * Fetch single video by ID. Not-found/cross-account arrives as an empty 200 — normalized to a 404.
   */
  static async getVideo(cId: number, mediaId: number): Promise<VideoViewData> {
    const res = await webApi.get<VideoViewData>(`media/video/${mediaId}`, { cId });
    return ApiError.requireFound(res, 'media', 'Video');
  }

  /**
   * Update video metadata.
   */
  static async updateVideo(
    cId: number,
    mediaId: number,
    data: VideoUpdateRequest
  ): Promise<VideoViewData> {
    return webApi.post<VideoViewData>(
      `media/video/${mediaId}`,
      { cId },
      data
    );
  }

  /**
   * Create a clip from an existing video.
   */
  static async createClip(
    cId: number,
    mediaId: number,
    req: ClipCreateRequest
  ): Promise<VideoViewData> {
    return webApi.post<VideoViewData>(`media/video/${mediaId}/clip`, { cId }, req);
  }

  /**
   * Get available transcode VOD presets.
   */
  static async getTranscodeVods(cId: number): Promise<IListRes<TranscodeVodData>> {
    return webApi.get<IListRes<TranscodeVodData>>('transcodevod', { cId });
  }

  /**
   * Create a text track record and get S3 upload key.
   */
  static async createTextTrack(
    cId: number,
    mediaId: number,
    req: TextTrackCreateRequest
  ): Promise<ClientIngest> {
    return webApi.post<ClientIngest>(`media/${mediaId}/texttrack`, { cId }, req);
  }

  /**
   * Delete a text track.
   */
  static async deleteTextTrack(
    cId: number,
    mediaId: number,
    fileId: number
  ): Promise<void> {
    return webApi.delete<void>(`media/${mediaId}/texttrack/${fileId}`, { cId });
  }

  /** Activate videos (batch). */
  static async activateVideos(cId: number, mediaIds: number[]): Promise<void> {
    return webApi.post('media/activate', { cId }, { mediaIds });
  }

  /** Deactivate videos (batch). */
  static async deactivateVideos(cId: number, mediaIds: number[]): Promise<void> {
    return webApi.post('media/deactivate', { cId }, { mediaIds });
  }

  /** Delete videos (batch). */
  static async deleteVideos(cId: number, mediaIds: number[]): Promise<void> {
    return webApi.post('media/delete', { cId }, { mediaIds });
  }

  /** Create poster ingest record and get S3 upload key. */
  static async createPoster(
    cId: number,
    mediaId: number,
    req: MediaPosterIngestRequest
  ): Promise<ClientIngest> {
    return webApi.post<ClientIngest>(`media/${mediaId}/ingestposter`, { cId }, req);
  }

  /** Extract poster from video frame at timestamp. */
  static async extractPoster(
    cId: number,
    mediaId: number,
    fileId: number,
    req: IExtractMediaPosterReq
  ): Promise<ClientIngest> {
    return webApi.post<ClientIngest>(
      `media/${mediaId}/extractposter/${fileId}`,
      { cId },
      req
    );
  }

  /** Delete poster and thumbnail. */
  static async deletePoster(cId: number, mediaId: number): Promise<void> {
    return webApi.delete<void>(`media/${mediaId}/poster`, { cId });
  }

  /** Create a merged video from multiple source videos. */
  static async createMerge(cId: number, req: MergeCreateRequest): Promise<VideoViewData> {
    return webApi.post<VideoViewData>('media/video/merge', { cId }, req);
  }

  /** Batch add/remove tags across many media in one set-based server call. */
  static async batchUpdateTags(cId: number, req: MediaTagsBatchRequest): Promise<MediaTagsBatchResponse> {
    return webApi.post<MediaTagsBatchResponse>('media/tags/batch', { cId }, req);
  }
}
